import * as React from 'react';
import type {CustomFormField} from 'src/types/crm';
import {
  FileUpload,
  FileBlock,
} from '@spaced-out/ui-design-system/lib/components/FileUpload';
import type {FileUploadRef} from '@spaced-out/ui-design-system/lib/components/FileUpload';
import {
  Toast,
  toastApi,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import classify from 'src/utils/classify';
import {VALID_ATTACHMENTS} from 'src/utils/crm';
import {useSelector} from 'react-redux';
import {selectUserInfo} from 'src/selectors/crm';
import {integrationTypes} from 'src/components/crm/constant';

import * as api from 'src/utils/api-no-store-crm';

import css from './index.css';


type FormikFieldType = any;

type FileUploadComponentProps = {
  formikField: FormikFieldType;
  field: CustomFormField;
  onChange: (arg1: string, arg2: unknown) => void;
  isRequired: boolean;
  isMultiFile: boolean;
  error: string | null | undefined;
  touched: boolean;
  label: string;
  classNames?: string;
  value?: string | null;
  disabled?: boolean;
};

type FileUploadCallbacks = {
  onProgress: (arg1: number) => void;
  onSuccess: () => void;
  onError: (arg1: string) => void;
};

// Pure utility functions (not using hooks)
const validateFileSizeInMB = (
  file: any,
  minMB: undefined,
  maxMB: undefined,
) => {
  // 1. Check that "file" is actually a File
  if (!(file instanceof File)) {
    return 'Invalid input: expected a File object.';
  }

  // 2. Compute size in megabytes (with two decimal places for readability)
  const sizeMB = file.size / (1024 * 1024); // bytes → MB

  if (minMB && maxMB) {
    if (sizeMB < minMB) {
      return `File is too small (${sizeMB.toFixed(
        2,
      )} MB). Minimum allowed is ${minMB} MB.`;
    }
    if (sizeMB > maxMB) {
      return `File is too large (${sizeMB.toFixed(
        2,
      )} MB). Maximum allowed is ${maxMB} MB.`;
    }
  }
  // 3. If a minimum bound was provided, ensure sizeMB ≥ minMB
  if (typeof minMB === 'number') {
    if (sizeMB < minMB) {
      return `File is too small (${sizeMB.toFixed(
        2,
      )} MB). Minimum allowed is ${minMB} MB.`;
    }
  }

  // 4. If a maximum bound was provided, ensure sizeMB ≤ maxMB
  if (typeof maxMB === 'number') {
    if (sizeMB > maxMB) {
      return `File is too large (${sizeMB.toFixed(
        2,
      )} MB). Maximum allowed is ${maxMB} MB.`;
    }
  }

  // 5. Passed all applicable checks
  return true;
};

function validateFileExtension(
  input: any,
  allowedExtsCsv: undefined,
  caseSensitive = false,
) {
  // 1) Validate that allowedExtsCsv is a non-empty string
  // @ts-expect-error - TS2339 - Property 'trim' does not exist on type 'never'.
  if (typeof allowedExtsCsv !== 'string' || allowedExtsCsv.trim() === '') {
    return {
      valid: false,
      error:
        'Allowed extensions must be provided as a non-empty comma-separated string, ' +
        'e.g. ".pdf,.docx,.png".',
    };
  }

  // 2) Parse the CSV into an array of trimmed extensions (each starting with a dot)
  //    Example: ".docx,.pdf,.png" → [".docx", ".pdf", ".png"]
  // @ts-expect-error - TS2339 - Property 'split' does not exist on type 'never'.
  const rawList = allowedExtsCsv.split(',');
  const allowedList = rawList
    // @ts-expect-error - TS7006 - Parameter 'ext' implicitly has an 'any' type.
    .map((ext) => ext.trim())
    // @ts-expect-error - TS7006 - Parameter 'ext' implicitly has an 'any' type.
    .filter((ext) => ext !== '');

  if (allowedList.length === 0) {
    return {
      valid: false,
      error:
        'No valid extensions found in the provided list. Make sure to separate ' +
        'each with a comma, e.g. \'.pdf,.docx,.png\'.',
    };
  }

  // 3) Determine the filename string from input
  let filename;
  if (typeof input === 'string') {
    filename = input;
  } else if (input instanceof File) {
    filename = input.name;
  } else {
    return {
      valid: false,
      error: 'Invalid argument: expected a File object or a filename string.',
    };
  }

  // 4) Extract the extension, including the dot (everything from the last dot to the end)
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
    return {
      valid: false,
      error: `File "${filename}" has no extension.`,
    };
  }
  let ext = filename.slice(lastDotIndex); // e.g. ".pdf" or ".DOCX"
  if (!caseSensitive) {
    ext = ext.toLowerCase();
  }

  // 5) Normalize the allowed-list for comparison
  // @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
  const normalizedAllowed = allowedList.map((e) =>
    caseSensitive ? e : e.toLowerCase(),
  );

  // 6) Check if the extracted extension is in the allowed list
  if (!normalizedAllowed.includes(ext)) {
    return {
      valid: false,
      error: `Invalid file extension "${ext}". Allowed extensions: (${allowedList.join(
        ', ',
      )}).`,
    };
  }

  // 7) Passed all checks
  return {valid: true};
}

export const FileUploadComponent = ({
  formikField,
  field,
  onChange,
  isRequired,
  isMultiFile,
  error,
  touched,
  label,
  classNames,
  value,
  disabled = false,
}: FileUploadComponentProps) => {
  const ref = React.useRef<FileUploadRef>(null);
  const {organization: organizationInfo} = useSelector(selectUserInfo) ?? {};
  const {integrationType = 'NONE'} = organizationInfo ?? {};
  const [fileIdToDelete, setFileIdToDelete] = React.useState('');
  const [localFiles, setLocalFiles] = React.useState(() => {
    if (!value) {
      return [];
    }
    if (typeof value === 'string') {
      return value.split(',').filter((v) => v && v.trim() !== '');
    }
    if (Array.isArray(value)) {
      return value;
    }
    return [];
  });

  // Initialize files state only once from formikField.value
  const [files, setFiles] = React.useState(() => {
    const initialValue = value;
    if (isMultiFile) {
      if (!initialValue) {
        return [];
      }
      if (typeof initialValue === 'string') {
        return initialValue.split(',').filter((v) => v && v.trim() !== '');
      }
      if (Array.isArray(initialValue)) {
        return initialValue;
      }
      return [];
    } else {
      // Single file mode
      if (!initialValue) {
        return [];
      }
      if (Array.isArray(initialValue)) {
        return initialValue;
      }
      return [initialValue];
    }
  });

  // Sync internal state when value prop is cleared externally (e.g., clear button)
  React.useEffect(() => {
    // Only handle external clear (when value becomes empty but files still exist)
    if (
      (value === null || value === '' || value === undefined) &&
      (files.length > 0 || localFiles.length > 0)
    ) {
      // Clear local state
      setFiles([]);
      setLocalFiles([]);

      // Clear FileUpload component's internal files
      // Create a copy of the array to avoid mutation issues during iteration
      const internalFiles = [...(ref.current?.files || [])];
      internalFiles.forEach((file) => {
        ref.current?.handleFileClear(file.id);
      });
    }
  }, [value]); // Intentionally not including files/localFiles to avoid loops

  // Show toast error helper function
  const showErrorToast = React.useCallback((message: string) => {
    const errorToast = (
      <Toast initialFocus={0} semantic={'danger'}>
        <ToastTitle>{message}</ToastTitle>
      </Toast>
    );
    toastApi.show(errorToast);
  }, []);

  // Memoize uploadMedia function to prevent recreating on each render
  const uploadMedia = React.useCallback(
    (
      file: File,
    ): Promise<{
      url: string;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      return api
        .post('/api/upload-attachment', formData, undefined, {
          multipart: true,
          apiPath: '/trmapi',
        })
        .then((response) => {
          console.log(response, 'upload');
          return response;
        })
        .catch((error) => {
          console.log(error);
          throw error;
        });
    },
    [],
  );

  const mockState = React.useCallback((id: string) => {
    let progress = 0;
    const intervalId = setInterval(() => {
      progress += 5;
      if (progress >= 100) {
        ref.current?.moveFileToSuccess(id);
        clearInterval(intervalId);
      } else {
        ref.current?.moveFileToProgress(id, progress);
      }
    }, 300);
  }, []);

  // Handle file validation and upload
  const handleFileValidation = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'file' implicitly has an 'any' type.
    async (file) => {
      if (field.validation && field.validation.type === 'MAXIMUM_FILE_SIZE') {
        const validation = validateFileSizeInMB(
          file.file,
          // @ts-expect-error - TS2339 - Property 'min' does not exist on type '{ absoluteValue: number; } & { absoluteValue: number; }'.
          field.validation.value.min,
          // @ts-expect-error - TS2339 - Property 'max' does not exist on type '{ absoluteValue: number; } & { absoluteValue: number; }'.
          field.validation.value.max,
        );
        if (typeof validation === 'string') {
          showErrorToast(validation);
          return false;
        }
        return true;
      } else if (
        field.validation &&
        field.validation.type === 'EXTENSION_TYPE'
      ) {
        const validationResult = validateFileExtension(
          file.file,
          // @ts-expect-error - TS2339 - Property 'extensionType' does not exist on type '{ absoluteValue: number; } & { absoluteValue: number; }'.
          field.validation.value.extensionType,
        );
        if (!validationResult.valid && validationResult.error) {
          showErrorToast(validationResult.error);
          return false;
        }
        return true;
      }
      return true;
    },
    [field.validation, showErrorToast],
  );

  // Handle file upload
  const handleFileUpload = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'file' implicitly has an 'any' type.
    async (file) => {
      mockState(file.id);
      const response = await uploadMedia(file.file);
      if (isMultiFile) {
        setFiles((prevFiles) => [...prevFiles, response]);
      } else {
        // @ts-expect-error - TS2322 - Type '{ url: string; }' is not assignable to type 'never'.
        setFiles([response]);
      }
    },
    [mockState, uploadMedia, isMultiFile],
  );

  // Update formik field when files change - but use a ref to prevent infinite updates
  const filesRef = React.useRef(files);

  React.useEffect(() => {
    // Only update if files have actually changed
    if (filesRef.current !== files) {
      filesRef.current = files;
      // For single file, send the first file or null if empty
      // For multi file, send the entire array
      const value = isMultiFile
        ? files
        : files?.length && files.length > 0
        ? files[0]
        : null;
      onChange(formikField.name, value);
    }
  }, [files, formikField.name, onChange, isMultiFile]);

  // Use callbacks with stable references to avoid dependency changes
  const handleRejectedFilesDrop = React.useCallback(() => {
    if (isMultiFile) {
      // @ts-expect-error - TS2339 - Property 'files' does not exist on type 'never'.
      setFiles(ref.current?.files || []);
    } else {
      setFiles([]);
    }
    setLocalFiles([]);
  }, [isMultiFile]);

  const handleFileClear = (fileId?: string) => {
    if (isMultiFile) {
      // For multi-file uploads, remove specific file if fileId is provided
      if (fileId) {
        setFiles((prevFiles) =>
          prevFiles.filter((_, index) => `file-${index}` !== fileId),
        );
        setLocalFiles((prevFiles) =>
          prevFiles.filter((_, index) => `file-${index}` !== fileId),
        );
        onChange(
          formikField.name,
          files?.filter((_, index) => `file-${index}` !== fileId).join(','),
        );
      } else {
        // Clear all files if no fileId provided

        setFiles([]);
        setLocalFiles([]);
        onChange(formikField.name, '');
      }
    } else {
      // For single file upload, clear the file
      setFiles([]);
      setLocalFiles([]);
      onChange(formikField.name, '');
    }
  };

  const handleValidFilesDrop = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'fileObjectArray' implicitly has an 'any' type. | TS7006 - Parameter 'callbacks' implicitly has an 'any' type.
    async (fileObjectArray, callbacks) => {
      try {
        for (const file of fileObjectArray) {
          const isValid = await handleFileValidation(file);
          if (isValid) {
            await handleFileUpload(file);
            // callbacks?.onSuccess?.();
          } else {
          }
        }
      } catch (error: any) {
        console.error(error);
        // callbacks?.onError?.(error.message);
      }
    },
    [handleFileValidation, handleFileUpload],
  );

  return (
    <div className={classify(css.fileUploadAreaContainer, classNames)}>
      <FileUpload
        {...formikField}
        label={label}
        ref={ref}
        placeholder={field.customField.placeholder}
        maxFiles={isMultiFile ? 10 : 1}
        required={isRequired}
        accept={
          // @ts-expect-error - TS2339 - Property 'extensionType' does not exist on type '{ absoluteValue: number; } & { absoluteValue: number; }'.
          field.validation?.value?.extensionType
            ? {
                'application/octet-stream': [
                  // @ts-expect-error - TS2339 - Property 'extensionType' does not exist on type '{ absoluteValue: number; } & { absoluteValue: number; }'.
                  field.validation?.value?.extensionType,
                ],
              }
            : {...VALID_ATTACHMENTS}
        }
        maxSize={
          integrationType === integrationTypes.SUCCESSFACTORS
            ? 10000000
            : 100000000
        }
        onValidFilesDrop={handleValidFilesDrop}
        onRejectedFilesDrop={handleRejectedFilesDrop}
        onFileClear={handleFileClear}
        error={error && touched}
        disabled={disabled}
        errorText={error}
      />
      {/* file upload will handle the newly uploaded files fileupload internally use fileblock to display the files*/}
      {localFiles
        ?.filter((file) => file && file !== null && file !== 'null')
        ?.map((file, index) => {
          const trimmedFile = file.trim();
          if (!trimmedFile) {
            return null;
          }

          // Extract just the filename after the last underscore
          const displayName = trimmedFile.split('__').pop() || trimmedFile;

          return (
            <div key={index}>
              {/* @ts-expect-error - TS2769 - No overload matches this call. */}
              <FileBlock
                fileObject={{
                  file: {
                    name: displayName,
                  },
                  id: `file-${index}`,
                  progress: null,
                  reject: false,
                  rejectReason: '',
                  showReUpload: false,
                  success: false,
                }}
                handleFileClear={() => handleFileClear(`file-${index}`)}
              />
            </div>
          );
        })}
    </div>
  );
};
